import { Reducer } from 'redux'
import { SavedPrompt } from './promptsApi'

export type AgentStage =
  | 'thinking'
  | 'retrieving'
  | 'using_tool'
  | 'processing'
  | 'responding'
  | 'complete'

export interface CompletedStage {
  stage: AgentStage
  message: string
  timestamp: string
}

/**
 * How the panel is placed. The host sets the opening layout; the user changes it
 * from the header, and their choice wins from then on.
 */
export type LabraChatLayout = 'floating' | 'sidebar' | 'expanded'

/** The two lists reachable from the welcome counts. */
export type LabraChatList = 'todos' | 'recommendations'

/** A file attached to a user message, rendered as a downloadable pill. */
export interface LabraChatMessageDocument {
  filename: string
  s3_uri: string
}

/** Lifecycle of a single funding-run step within the progress stepper. */
export type FundingStepStatus = 'pending' | 'running' | 'done' | 'failed'

/** One step of a funding run, from the `funding_plan` SSE frame. `label` is
 * server-provided display text — never hardcoded client-side. */
export interface FundingProgressStep {
  step: string
  label: string
  status: FundingStepStatus
  /** Optional sub-activity text for the active step (`funding_step.detail`). */
  detail?: string
}

/**
 * Determinate progress of a funding run (POC / MPOPP), driven entirely by the
 * `funding_plan` (skeleton) and `funding_step` (advance) SSE frames during a
 * chat turn. Attached to the assistant message that triggered the run.
 */
export interface FundingProgress {
  /** Tool that drives this run, e.g. 'start_poc_run'. */
  tool: string
  /** Program key from the plan frame, e.g. 'poc' | 'mpopp'. */
  program: string
  /** Opportunity this run is for (name or id), shown as the card subtitle when
   * the plan frame provides it. Absent when the backend omits it. */
  opportunityName?: string
  /** Ordered steps; statuses advance as `funding_step` frames arrive. */
  steps: FundingProgressStep[]
  /** True for `resume_poc_run`, which submits directly without replaying
   * stages — the card shows an indeterminate "Submitting…" state instead. */
  indeterminate: boolean
  /** Set once the run's tool ends (or the turn finishes), so the card freezes
   * its final state even when a run stops before reaching 100%. */
  ended: boolean
}

export interface LabraChatMessage {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  isStreaming: boolean
  agentStage: AgentStage | null
  stageMessage: string | null
  completedStages: CompletedStage[]
  timestamp: string
  /** Reveal has caught up and settled — safe to render full markdown even while
   * the stream is still technically open (see streamTypewriter `onIdle`). */
  streamSettled?: boolean
  /** Label of a tool currently running (e.g. "Checking funding runs"), shown
   * while the assistant pauses to call a tool. Null when no tool is active. */
  activeTool?: string | null
  /** Files the user attached to this message, shown as pills under the text. */
  documents?: LabraChatMessageDocument[]
  /** Determinate progress of a funding run triggered by this turn, rendered as
   * a stepper card. Present only while/after a funding tool runs. */
  fundingProgress?: FundingProgress
  /** OpenUI-Lang emitted by a `render_ui` tool call this turn, rendered as
   * generative UI (react-ui components). Present only when the agent produced a
   * visual; the streamed text remains the conversational answer alongside it. */
  openUiLang?: string
  /** HTML email draft from `draft_email` (`content_type: email_html`). */
  emailPreview?: LabraChatEmailPreview
  /** Proposed AI schedule fields shown as a Schedule Details card in chat. */
  scheduleDetails?: LabraChatScheduleDetails
}

/** Figma 714:3485 — draft from `draft_email` for in-chat preview / send. */
export interface LabraChatEmailPreview {
  draftId: string
  subject: string
  html: string
  recipientEmail?: string | null
}

/** Figma 641:3092 — Schedule Details card fields. */
export interface LabraChatScheduleDetails {
  reportName: string
  frequency: string
  timezone: string
  recipients: string[]
  delivery: string
  /** Optional start date shown as "Starts on". */
  startsOn?: string
}

/**
 * A follow-up action surfaced by the agent at the end of a response
 * (`next_best_actions` stream event). `label` is the button text, `prompt` is
 * the message sent when the button is clicked.
 */
export interface NextBestAction {
  label: string
  prompt: string
}

export interface LabraChatState {
  messages: LabraChatMessage[]
  sessionId: string
  /** Backend agent selector sent on the chat stream body (e.g. `cosell_agent`).
   * Empty string means the key is omitted so the backend picks its default. */
  agentType: string
  isLoading: boolean
  isPanelOpen: boolean
  isFullScreen: boolean
  nextBestActions: NextBestAction[]
  /** True while the answer has streamed and the agent is computing its
   * follow-up actions — drives the "Generating next best actions" affordance
   * shown in the suggestion-chip slot until the chips arrive. */
  isGeneratingNextActions: boolean
  /** True while the chat-history list is shown in place of the conversation. */
  isHistoryOpen: boolean
  /** The user dismissed the page-context pill, so this session sends no context. */
  isPageContextDismissed: boolean
  /** Page context already went out with this session's first message. It is a
   * one-shot: the agent needs to know where the conversation started, not to be
   * re-told on every turn. */
  isPageContextSent: boolean
  /** Attachment shown in the side-by-side preview, or null when none is open.
   * Only the full-screen layout has room for it. */
  previewDocument: LabraChatMessageDocument | null
  /** Centered Email preview modal (Figma 714:3485), or null when closed. */
  previewEmail: LabraChatEmailPreview | null
  /** Null until the user picks a layout, so the host's own choice still holds. */
  layout: LabraChatLayout | null
  /** The list shown in place of the conversation, or null for the chat itself. */
  openList: LabraChatList | null
  /** The list this conversation was started from, so back returns there. */
  listOrigin: LabraChatList | null
  /** The user's saved prompts, newest-first. Loaded once when the panel opens. */
  savedPrompts: SavedPrompt[]
  /** True once the list has been fetched, so re-opening the menu does not refetch. */
  areSavedPromptsLoaded: boolean
  /**
   * One-shot draft for the composer (e.g. sample prompt insert). The input bar
   * consumes and clears it so the user can edit before sending.
   */
  composerDraft: string | null
}

export enum LabraChatActionTypes {
  ADD_MESSAGE = 'LABRA_CHAT_ADD_MESSAGE',
  UPDATE_MESSAGE = 'LABRA_CHAT_UPDATE_MESSAGE',
  SET_MESSAGES = 'LABRA_CHAT_SET_MESSAGES',
  CLEAR_MESSAGES = 'LABRA_CHAT_CLEAR_MESSAGES',
  SET_SESSION_ID = 'LABRA_CHAT_SET_SESSION_ID',
  SET_AGENT_TYPE = 'LABRA_CHAT_SET_AGENT_TYPE',
  SET_LOADING = 'LABRA_CHAT_SET_LOADING',
  OPEN_PANEL = 'LABRA_CHAT_OPEN_PANEL',
  CLOSE_PANEL = 'LABRA_CHAT_CLOSE_PANEL',
  FINALIZE_STREAMING = 'LABRA_CHAT_FINALIZE_STREAMING',
  TOGGLE_FULLSCREEN = 'LABRA_CHAT_TOGGLE_FULLSCREEN',
  SET_NEXT_BEST_ACTIONS = 'LABRA_CHAT_SET_NEXT_BEST_ACTIONS',
  SET_GENERATING_NEXT_ACTIONS = 'LABRA_CHAT_SET_GENERATING_NEXT_ACTIONS',
  OPEN_HISTORY = 'LABRA_CHAT_OPEN_HISTORY',
  CLOSE_HISTORY = 'LABRA_CHAT_CLOSE_HISTORY',
  DISMISS_PAGE_CONTEXT = 'LABRA_CHAT_DISMISS_PAGE_CONTEXT',
  SET_PAGE_CONTEXT_SENT = 'LABRA_CHAT_SET_PAGE_CONTEXT_SENT',
  RESET_PAGE_CONTEXT = 'LABRA_CHAT_RESET_PAGE_CONTEXT',
  OPEN_PREVIEW = 'LABRA_CHAT_OPEN_PREVIEW',
  CLOSE_PREVIEW = 'LABRA_CHAT_CLOSE_PREVIEW',
  OPEN_EMAIL_PREVIEW = 'LABRA_CHAT_OPEN_EMAIL_PREVIEW',
  CLOSE_EMAIL_PREVIEW = 'LABRA_CHAT_CLOSE_EMAIL_PREVIEW',
  SET_LAYOUT = 'LABRA_CHAT_SET_LAYOUT',
  SET_OPEN_LIST = 'LABRA_CHAT_SET_OPEN_LIST',
  SET_LIST_ORIGIN = 'LABRA_CHAT_SET_LIST_ORIGIN',
  SET_SAVED_PROMPTS = 'LABRA_CHAT_SET_SAVED_PROMPTS',
  UPSERT_SAVED_PROMPT = 'LABRA_CHAT_UPSERT_SAVED_PROMPT',
  REMOVE_SAVED_PROMPT = 'LABRA_CHAT_REMOVE_SAVED_PROMPT',
  SET_COMPOSER_DRAFT = 'LABRA_CHAT_SET_COMPOSER_DRAFT',
}

type LabraChatAction =
  | { type: LabraChatActionTypes.ADD_MESSAGE; payload: LabraChatMessage }
  | {
      type: LabraChatActionTypes.UPDATE_MESSAGE
      payload: { id: string; updates: Partial<LabraChatMessage> }
    }
  | { type: LabraChatActionTypes.SET_MESSAGES; payload: LabraChatMessage[] }
  | { type: LabraChatActionTypes.CLEAR_MESSAGES }
  | { type: LabraChatActionTypes.SET_SESSION_ID; payload: string }
  | { type: LabraChatActionTypes.SET_AGENT_TYPE; payload: string }
  | { type: LabraChatActionTypes.SET_LOADING; payload: boolean }
  | { type: LabraChatActionTypes.OPEN_PANEL }
  | { type: LabraChatActionTypes.CLOSE_PANEL }
  | { type: LabraChatActionTypes.FINALIZE_STREAMING }
  | { type: LabraChatActionTypes.TOGGLE_FULLSCREEN }
  | {
      type: LabraChatActionTypes.SET_NEXT_BEST_ACTIONS
      payload: NextBestAction[]
    }
  | {
      type: LabraChatActionTypes.SET_GENERATING_NEXT_ACTIONS
      payload: boolean
    }
  | { type: LabraChatActionTypes.OPEN_HISTORY }
  | { type: LabraChatActionTypes.CLOSE_HISTORY }
  | { type: LabraChatActionTypes.DISMISS_PAGE_CONTEXT }
  | { type: LabraChatActionTypes.SET_PAGE_CONTEXT_SENT }
  | { type: LabraChatActionTypes.RESET_PAGE_CONTEXT }
  | {
      type: LabraChatActionTypes.OPEN_PREVIEW
      payload: LabraChatMessageDocument
    }
  | { type: LabraChatActionTypes.CLOSE_PREVIEW }
  | {
      type: LabraChatActionTypes.OPEN_EMAIL_PREVIEW
      payload: LabraChatEmailPreview
    }
  | { type: LabraChatActionTypes.CLOSE_EMAIL_PREVIEW }
  | { type: LabraChatActionTypes.SET_LAYOUT; payload: LabraChatLayout }
  | {
      type: LabraChatActionTypes.SET_OPEN_LIST
      payload: LabraChatList | null
    }
  | {
      type: LabraChatActionTypes.SET_LIST_ORIGIN
      payload: LabraChatList | null
    }
  | { type: LabraChatActionTypes.SET_SAVED_PROMPTS; payload: SavedPrompt[] }
  | { type: LabraChatActionTypes.UPSERT_SAVED_PROMPT; payload: SavedPrompt }
  | { type: LabraChatActionTypes.REMOVE_SAVED_PROMPT; payload: string }
  | { type: LabraChatActionTypes.SET_COMPOSER_DRAFT; payload: string | null }

export const initialState: LabraChatState = {
  messages: [],
  sessionId: '',
  agentType: '',
  isLoading: false,
  isPanelOpen: false,
  isFullScreen: false,
  nextBestActions: [],
  isGeneratingNextActions: false,
  isHistoryOpen: false,
  isPageContextDismissed: false,
  isPageContextSent: false,
  previewDocument: null,
  previewEmail: null,
  layout: null,
  openList: null,
  listOrigin: null,
  savedPrompts: [],
  areSavedPromptsLoaded: false,
  composerDraft: null,
}

export const reducer: Reducer<LabraChatState, LabraChatAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case LabraChatActionTypes.ADD_MESSAGE:
      return { ...state, messages: [...state.messages, action.payload] }
    case LabraChatActionTypes.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.payload.id
            ? { ...msg, ...action.payload.updates }
            : msg
        ),
      }
    case LabraChatActionTypes.SET_MESSAGES:
      return { ...state, messages: action.payload }
    case LabraChatActionTypes.CLEAR_MESSAGES:
      // A fresh chat is not "from" anywhere, so back goes to the welcome again.
      return {
        ...state,
        messages: [],
        previewDocument: null,
        previewEmail: null,
        openList: null,
        listOrigin: null,
        composerDraft: null,
      }
    case LabraChatActionTypes.DISMISS_PAGE_CONTEXT:
      return { ...state, isPageContextDismissed: true }
    case LabraChatActionTypes.SET_PAGE_CONTEXT_SENT:
      return { ...state, isPageContextSent: true }
    case LabraChatActionTypes.RESET_PAGE_CONTEXT:
      return {
        ...state,
        isPageContextDismissed: false,
        isPageContextSent: false,
      }
    case LabraChatActionTypes.SET_SESSION_ID:
      return { ...state, sessionId: action.payload }
    case LabraChatActionTypes.SET_AGENT_TYPE:
      return { ...state, agentType: action.payload }
    case LabraChatActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload }
    case LabraChatActionTypes.OPEN_PANEL:
      return { ...state, isPanelOpen: true }
    case LabraChatActionTypes.CLOSE_PANEL:
      return {
        ...state,
        isPanelOpen: false,
        isFullScreen: false,
        isHistoryOpen: false,
        previewDocument: null,
        previewEmail: null,
        composerDraft: null,
      }
    case LabraChatActionTypes.TOGGLE_FULLSCREEN: {
      const isFullScreen = !state.isFullScreen
      return {
        ...state,
        isFullScreen,
        previewDocument: isFullScreen ? state.previewDocument : null,
        previewEmail: isFullScreen ? state.previewEmail : null,
      }
    }
    case LabraChatActionTypes.SET_NEXT_BEST_ACTIONS:
      return { ...state, nextBestActions: action.payload }
    case LabraChatActionTypes.SET_GENERATING_NEXT_ACTIONS:
      return { ...state, isGeneratingNextActions: action.payload }
    case LabraChatActionTypes.OPEN_HISTORY:
      return {
        ...state,
        isHistoryOpen: true,
        previewDocument: null,
        previewEmail: null,
      }
    case LabraChatActionTypes.CLOSE_HISTORY:
      return { ...state, isHistoryOpen: false }
    case LabraChatActionTypes.OPEN_PREVIEW:
      return {
        ...state,
        previewDocument: action.payload,
        isFullScreen: true,
        previewEmail: null,
      }
    case LabraChatActionTypes.CLOSE_PREVIEW:
      return { ...state, previewDocument: null }
    case LabraChatActionTypes.OPEN_EMAIL_PREVIEW:
      return {
        ...state,
        previewEmail: action.payload,
        isFullScreen: true,
        previewDocument: null,
      }
    case LabraChatActionTypes.CLOSE_EMAIL_PREVIEW:
      return { ...state, previewEmail: null }
    case LabraChatActionTypes.SET_LAYOUT: {
      const isFullScreen = action.payload === 'expanded'
      return {
        ...state,
        layout: action.payload,
        isFullScreen,
        previewDocument: isFullScreen ? state.previewDocument : null,
        previewEmail: isFullScreen ? state.previewEmail : null,
      }
    }
    case LabraChatActionTypes.SET_OPEN_LIST:
      return { ...state, openList: action.payload }
    case LabraChatActionTypes.SET_LIST_ORIGIN:
      return { ...state, listOrigin: action.payload }
    case LabraChatActionTypes.SET_SAVED_PROMPTS:
      return {
        ...state,
        savedPrompts: action.payload,
        areSavedPromptsLoaded: true,
      }
    case LabraChatActionTypes.UPSERT_SAVED_PROMPT: {
      // A repeat save returns the row that already exists, so match on id
      // rather than appending a duplicate card.
      const saved = action.payload
      const isKnown = state.savedPrompts.some(prompt => prompt.id === saved.id)
      return {
        ...state,
        savedPrompts: isKnown
          ? state.savedPrompts.map(prompt =>
              prompt.id === saved.id ? saved : prompt
            )
          : [saved, ...state.savedPrompts],
      }
    }
    case LabraChatActionTypes.REMOVE_SAVED_PROMPT:
      return {
        ...state,
        savedPrompts: state.savedPrompts.filter(
          prompt => prompt.id !== action.payload
        ),
      }
    case LabraChatActionTypes.SET_COMPOSER_DRAFT:
      return { ...state, composerDraft: action.payload }
    case LabraChatActionTypes.FINALIZE_STREAMING:
      // Stop any orphaned in-flight assistant message from spinning forever
      // (e.g. when a new message is sent before the previous one finished).
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.isStreaming
            ? {
                ...msg,
                isStreaming: false,
                agentStage: 'complete',
                stageMessage: null,
              }
            : msg
        ),
      }
    default:
      return state
  }
}
